import { useCallback, useEffect, useState } from 'react'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import {
  GeneratedSprint,
  GenerateSprintsParams,
  LabelParams,
  SprintInterval,
  appendSprints,
  generateSprints,
  relabelSprints,
  shiftSprintDates,
} from './generateSprints'

const SHIFT_DAYS = 7

type Params = {
  interval: SprintInterval
  startDate: Date | undefined
  count: number
  labels: LabelParams
}

export const useSprintPreview = ({
  interval,
  startDate,
  count,
  labels,
}: Params) => {
  const [preview, setPreview] = useState<GeneratedSprint[]>([])

  const buildParams = useCallback(
    (targetCount: number): GenerateSprintsParams => {
      const dateStr = dateV2Service.format(startDate!)
      return interval === '1month'
        ? {
            ...labels,
            interval,
            startDayOfMonth: startDate!.getDate(),
            startDate: dateStr,
            count: targetCount,
          }
        : { ...labels, interval, startDate: dateStr, count: targetCount }
    },
    [interval, startDate, labels]
  )

  // 日付に影響する条件が変わったら全再生成する。入力途中に一瞬エラーになっただけで
  // 作り直すと「+1w」で手調整した日付が失われるため、hasError は条件に入れない
  // （エラー中は追加ボタンが disabled なので、プレビューが残っていても追加はされない）
  useEffect(() => {
    if (!startDate) {
      setPreview([])
      return
    }
    setPreview(generateSprints(buildParams(count)))
    // 作成数と命名設定は日付を作り直す必要がなく、別の useEffect で差分反映する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, startDate])

  // 作成数が変わったら末尾を追加/削除（日付調整を保持）
  useEffect(() => {
    if (!startDate) return
    setPreview(prev => {
      if (count <= 0) return []
      if (count === prev.length) return prev
      if (count < prev.length) return prev.slice(0, count)
      if (prev.length === 0) return generateSprints(buildParams(count))
      return appendSprints(
        prev,
        count - prev.length,
        interval,
        labels,
        startDate.getDate()
      )
    })
  }, [count, startDate, interval, labels, buildParams])

  // 命名設定が変わっても日付は変わらないため、採番し直すだけにする
  useEffect(() => {
    setPreview(prev =>
      prev.length === 0 ? prev : relabelSprints(prev, labels)
    )
  }, [labels])

  const shift = useCallback(
    (index: number, direction: 1 | -1) => {
      setPreview(prev => {
        if (prev.length === 0) return prev
        return shiftSprintDates(prev, index, direction * SHIFT_DAYS, labels)
      })
    },
    [labels]
  )

  const clear = useCallback(() => setPreview([]), [])

  return { preview, shift, clear }
}
